import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPen, faCheck, faXmark, faLayerGroup, faPlus, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import {
    createGroup, deleteGroup, getGroups, updateGroup, reorderGroupItems,
    getGroupAdvices, searchDoctorAdvices, addAdviceToGroup, removeAdviceFromGroup,
    getGroupLabTests, searchDoctorLabTests, addLabTestToGroup, removeLabTestFromGroup,
} from '../../../service/api/group';
import { addDoctorAdvice, addLabTest } from '../../../service/api/medical-data';
import { showSuccessToast } from '../../../utils/toaster-alert';
import CustomModal from '../modal/modal';
import CreatableSearchSelectAntD from '../updated-input/createble-search-select/creatable-search-select-antd';
import { DraggableList } from '../draggable-list/drgabble-list';
import './group-manager.scss';

// Lets already-mounted group pickers (the "add all from a group" dropdowns in the
// prescription) refresh when groups are created/renamed/deleted elsewhere.
export const GROUPS_CHANGED_EVENT = 'shafa:groups-changed';
const emitGroupsChanged = (type) => window.dispatchEvent(new CustomEvent(GROUPS_CHANGED_EVENT, { detail: { type } }));

// Reusable: add more kinds here as grouping is extended (chiefComplaint, ...).
// Each entry supplies the per-kind API calls + UI strings; the component itself is generic.
export const GROUP_TYPES = [
    {
        key: 'advice',
        label: 'Advice',
        textKey: 'details',
        subtitle: 'Create named groups and configure which advice belong to each.',
        selectHint: 'Select a group to configure its advice.',
        memberTitle: 'Advice',
        emptyMembersText: 'No advice in this group yet.',
        searchPlaceholder: 'Search advice to add, or type a new one',
        addedMsg: 'Advice added',
        createdMsg: 'Advice created',
        removedMsg: 'Advice removed',
        deleteConfirmText: 'will be removed and untagged from any advice.',
        fetchGroupItems: (doctorId, groupId) => getGroupAdvices(doctorId, groupId),
        searchItems: (doctorId, search) => searchDoctorAdvices(doctorId, search),
        addToGroup: ({ itemId, doctorId, groupId }) => addAdviceToGroup({ adviceId: itemId, doctorId, groupId }),
        removeFromGroup: ({ itemId, doctorId, groupId }) => removeAdviceFromGroup({ adviceId: itemId, doctorId, groupId }),
        createInGroup: ({ doctorId, text, groupId }) => addDoctorAdvice({ doctorId, details: text, groups: [groupId] }),
    },
    {
        key: 'labTest',
        label: 'Investigation',
        textKey: 'name',
        subtitle: 'Create named groups and configure which investigations belong to each.',
        selectHint: 'Select a group to configure its investigations.',
        memberTitle: 'Investigations',
        emptyMembersText: 'No investigations in this group yet.',
        searchPlaceholder: 'Search investigation to add, or type a new one',
        addedMsg: 'Investigation added',
        createdMsg: 'Investigation created',
        removedMsg: 'Investigation removed',
        deleteConfirmText: 'will be removed and untagged from any investigations.',
        fetchGroupItems: (doctorId, groupId) => getGroupLabTests(doctorId, groupId),
        searchItems: (doctorId, search) => searchDoctorLabTests(doctorId, search),
        addToGroup: ({ itemId, doctorId, groupId }) => addLabTestToGroup({ labTestId: itemId, doctorId, groupId }),
        removeFromGroup: ({ itemId, doctorId, groupId }) => removeLabTestFromGroup({ labTestId: itemId, doctorId, groupId }),
        // NOTE: lab test create expects `doctor`, advice expects `doctorId` — absorbed here.
        createInGroup: ({ doctorId, text, groupId }) => addLabTest({ doctor: doctorId, name: text, groups: [groupId] }),
    },
];

// Master/detail group management for one (doctor, type): create/rename/delete
// groups on the left, configure the selected group's members on the right.
// Embeddable anywhere (profile Groups tab, section-gear modal, ...).
const GroupManager = ({ doctorId, type, onCountChange }) => {
    const typeConfig = GROUP_TYPES.find((t) => t.key === type);

    const [groups, setGroups] = useState([]);
    const [newName, setNewName] = useState('');
    const [editingId, setEditingId] = useState('');
    const [editingName, setEditingName] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupItems, setGroupItems] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);

    const loadGroups = async () => {
        if (!doctorId) return;
        const res = await getGroups(doctorId, type);
        if (res?.status === 200) {
            const list = res.data || [];
            setGroups(list);
            onCountChange?.(list.length);
        }
    };

    useEffect(() => {
        loadGroups();
        setSelectedGroup(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctorId, type]);

    // Load the selected group's items + a fresh copy of the library for the add box.
    const loadGroupDetail = async (group) => {
        if (!group?._id) return;
        const [inGroup, library] = await Promise.all([
            typeConfig.fetchGroupItems(doctorId, group._id),
            typeConfig.searchItems(doctorId, ''),
        ]);
        const members = inGroup?.status === 200 ? (inGroup.data || []) : [];
        setGroupItems(members);
        const memberIds = new Set(members.map((a) => a._id));
        const lib = library?.status === 200 ? (library.data || []) : [];
        setItemOptions(
            lib
                .filter((a) => a?.[typeConfig.textKey]?.trim() && !memberIds.has(a._id))
                .map((a) => ({ value: JSON.stringify({ _id: a._id, value: a[typeConfig.textKey] }), label: a[typeConfig.textKey] }))
        );
    };

    const selectGroup = (group) => {
        setSelectedGroup(group);
        loadGroupDetail(group);
    };

    const handleCreate = async () => {
        const name = newName.trim();
        if (!name) return;
        const res = await createGroup({ doctorId, name, type });
        if (res?.status === 200) {
            setNewName('');
            showSuccessToast('Group created');
            loadGroups();
            emitGroupsChanged(type);
        }
    };

    const handleRename = async (groupId) => {
        const name = editingName.trim();
        if (!name) return;
        const res = await updateGroup(groupId, name);
        if (res?.status === 200) {
            setEditingId('');
            setEditingName('');
            showSuccessToast('Group updated');
            loadGroups();
            emitGroupsChanged(type);
            if (selectedGroup?._id === groupId) setSelectedGroup({ ...selectedGroup, name });
        }
    };

    const handleDelete = async (groupId) => {
        const res = await deleteGroup(groupId);
        if (res?.status === 200) {
            showSuccessToast('Group deleted');
            setConfirmDelete(null);
            if (selectedGroup?._id === groupId) setSelectedGroup(null);
            loadGroups();
            emitGroupsChanged(type);
        }
    };

    // ---- group membership ----
    const handleAddExisting = async (parsed) => {
        if (!selectedGroup?._id || !parsed?._id) return;
        const res = await typeConfig.addToGroup({ itemId: parsed._id, doctorId, groupId: selectedGroup._id });
        if (res?.status === 200) {
            showSuccessToast(typeConfig.addedMsg);
            loadGroupDetail(selectedGroup);
        }
    };

    const handleCreateInGroup = async (text) => {
        if (!selectedGroup?._id || !text?.trim()) return;
        const res = await typeConfig.createInGroup({ doctorId, text: text.trim(), groupId: selectedGroup._id });
        if (res?.status === 200) {
            showSuccessToast(typeConfig.createdMsg);
            loadGroupDetail(selectedGroup);
        }
    };

    // Drag-sort the group's members. Applied locally first so the list doesn't
    // jump under the cursor, then persisted; a failed save re-reads the server order.
    const handleReorder = async (nextItems) => {
        if (!selectedGroup?._id) return;
        const previousItems = groupItems;
        setGroupItems(nextItems);
        const res = await reorderGroupItems(selectedGroup._id, nextItems.map((item) => item._id));
        if (res?.status !== 200) setGroupItems(previousItems);
    };

    const handleRemoveFromGroup = async (itemId) => {
        if (!selectedGroup?._id) return;
        const res = await typeConfig.removeFromGroup({ itemId, doctorId, groupId: selectedGroup._id });
        if (res?.status === 200) {
            showSuccessToast(typeConfig.removedMsg);
            loadGroupDetail(selectedGroup);
        }
    };

    if (!typeConfig) return null;

    return (
        <>
            <div className="groups-settings__layout">
                {/* LEFT: group list */}
                <div className="groups-settings__left">
                    <div className="groups-settings__add">
                        <input
                            placeholder="New group name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                        />
                        <button
                            type="button"
                            className="groups-settings__add-btn"
                            onClick={handleCreate}
                            disabled={!newName.trim()}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add
                        </button>
                    </div>

                    {groups?.length === 0 ? (
                        <div className="groups-settings__empty">
                            <FontAwesomeIcon icon={faLayerGroup} />
                            <span>No groups yet. Add one above.</span>
                        </div>
                    ) : (
                        <div className="groups-settings__list">
                            {groups.map((group) => (
                                <div
                                    key={group._id}
                                    className={`groups-settings__row ${selectedGroup?._id === group._id ? 'is-selected' : ''}`}
                                    onClick={() => editingId !== group._id && selectGroup(group)}
                                >
                                    {editingId === group._id ? (
                                        <>
                                            <input
                                                className="groups-settings__edit-input"
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleRename(group._id);
                                                    if (e.key === 'Escape') { setEditingId(''); setEditingName(''); }
                                                }}
                                                autoFocus
                                            />
                                            <div className="groups-settings__actions" style={{ opacity: 1 }}>
                                                <button type="button" className="groups-settings__icon-btn is-confirm" onClick={(e) => { e.stopPropagation(); handleRename(group._id); }} title="Save">
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </button>
                                                <button type="button" className="groups-settings__icon-btn" onClick={(e) => { e.stopPropagation(); setEditingId(''); setEditingName(''); }} title="Cancel">
                                                    <FontAwesomeIcon icon={faXmark} />
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <span className="groups-settings__name">{group.name}</span>
                                            <div className="groups-settings__actions">
                                                <button
                                                    type="button"
                                                    className="groups-settings__icon-btn"
                                                    onClick={(e) => { e.stopPropagation(); setEditingId(group._id); setEditingName(group.name || ''); }}
                                                    title="Rename"
                                                >
                                                    <FontAwesomeIcon icon={faPen} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="groups-settings__icon-btn is-danger"
                                                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(group); }}
                                                    title="Delete"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                                <FontAwesomeIcon className="groups-settings__chevron" icon={faAngleRight} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* RIGHT: selected group's items */}
                <div className="groups-settings__detail">
                    {!selectedGroup ? (
                        <div className="groups-settings__empty groups-settings__detail-empty">
                            <FontAwesomeIcon icon={faLayerGroup} />
                            <span>{typeConfig.selectHint}</span>
                        </div>
                    ) : (
                        <>
                            <div className="groups-settings__detail-head">
                                <h3>{typeConfig.memberTitle} in “{selectedGroup.name}”</h3>
                                <span className="groups-settings__count">{groupItems.length}</span>
                            </div>
                            <div className="groups-settings__add-advice">
                                <CreatableSearchSelectAntD
                                    placeHolder={typeConfig.searchPlaceholder}
                                    options={itemOptions}
                                    handleSubmit={handleAddExisting}
                                    handleCreate={handleCreateInGroup}
                                />
                            </div>
                            {groupItems.length === 0 ? (
                                <div className="groups-settings__empty">
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                    <span>{typeConfig.emptyMembersText}</span>
                                </div>
                            ) : (
                                <div className="groups-settings__advice-list">
                                    <DraggableList
                                        items={groupItems}
                                        onReorder={handleReorder}
                                        getItemId={(item) => item._id}
                                        renderItem={(item) => (
                                            <div className="groups-settings__advice-row">
                                                <span>{item[typeConfig.textKey]}</span>
                                                <button
                                                    type="button"
                                                    className="groups-settings__icon-btn is-danger"
                                                    title="Remove from group"
                                                    onClick={() => handleRemoveFromGroup(item._id)}
                                                >
                                                    <FontAwesomeIcon icon={faXmark} />
                                                </button>
                                            </div>
                                        )}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <CustomModal size="sm" show={!!confirmDelete} setShow={() => setConfirmDelete(null)} isHeaderHide={true}>
                <div className="groups-settings__confirm">
                    <h4>Delete group?</h4>
                    <p>“{confirmDelete?.name}” {typeConfig.deleteConfirmText} This can’t be undone.</p>
                    <div className="groups-settings__confirm-actions">
                        <button type="button" onClick={() => setConfirmDelete(null)}>Cancel</button>
                        <button type="button" className="danger" onClick={() => handleDelete(confirmDelete?._id)}>Delete</button>
                    </div>
                </div>
            </CustomModal>
        </>
    );
};

export default GroupManager;
